import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ClientProfile } from "../../interfaces/common";
import CustomButton from "../CustomButton";
import TextLink from "../../components/TextLink";
import infoIcon from "url:shared-assets/icons/white/information-circle.svg";
import chevronLeftIcon from "url:shared-assets/icons/chevronLeft.svg";
import profileIcon from "url:shared-assets/icons/profile.svg";
import LeadershipInfoInfoModal from "../../pages/MatchWithCoachPage/components/LeadershipInfoInfoModal";
import MaskedIcon from "../MaskedIcon";
import pictureCircleIcon from "url:shared-assets/icons/pictureCircleBase.svg";
import { getLanguage } from "../../lang/helpers";
import EditPicture from "../EditPicture/EditPicture";
import ClientFiles from "./ClientFiles";
import { useAtom } from "jotai";
import { pictureAtom } from "../../jotai_state";
import {setUserType} from "../../constants";
import {VectorFileInterface} from "../../pages/ProfilePage/interfaces";

interface Props {
  profile: ClientProfile;
  save: Function;
  standalone: boolean;
  customerFiles: Array<VectorFileInterface>
}

const leadershipLevels = ["non-managerial", "manager_team", "c_level"];

const Profile: React.FC<Props> = (props: Props) => {
  const navigate = useNavigate();

  const [inCompanySince, setInCompanySince] = useState<number>(
    props.profile.inCompanySince
  );
  const [currentRoleSince, setCurrentRoleSince] = useState<number>(
    props.profile.currentRoleSince
  );
  const [title, setTitle] = useState<string>(props.profile.title);
  const [roleDescription, setRoleDescription] = useState<string>(props.profile.roleDescription);
  const [aboutMe, setAboutMe] = useState<string>(props.profile.aboutMe);
  const [leadershipLevel, setLeadershipLevel] = useState<string>(
    props.profile.leadershipLevel
  );
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [picture, setPicture] = useState<string>(props.profile.picture);
  const [editPicture, setEditPicture] = useState<boolean>(false);
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const thisYear = new Date().getFullYear();
  const [navPicture, setNavPicture] = useAtom(pictureAtom);

  const makeRange = (from: number, to: number) => {
    const step = from > to ? -1 : 1;
    let length = from > to ? from - to : to - from;
    return Array.from({ length: length + 1 }, (v, k) => from + k * step);
  };

  const notValid = () => {
    if (inCompanySince === 0) return true;
    if (currentRoleSince === 0) return true;
    if (title.length < 1) return true;
    return false;
  };

  const save = () => {
    const newProfile: ClientProfile = props.profile;
    newProfile.inCompanySince = inCompanySince;
    newProfile.currentRoleSince = currentRoleSince;
    newProfile.leadershipLevel = leadershipLevel;
    newProfile.title = title;
    newProfile.roleDescription = roleDescription;
    newProfile.aboutMe = aboutMe;
    props.save(newProfile);
  };

  const closeEditPicture = (pic: string) => {
    if (pic && pic.length > 0) {
      setPicture(pic);
      setNavPicture(pic);
      props.profile.picture = pic;
    }
    setEditPicture(false);
  };

  return (
    <>
      {editPicture && (
        <EditPicture
          picture={picture}
          close={closeEditPicture}
          lang={lang}
          who={"client"}
        />
      )}

      {props.standalone ? (
        <>
          <div className="mt-6 z-heading2">{t("my-account")}</div>
          {
            props.profile.hasCoachUser &&
            <div className="mt-2 z-link" onClick={() => setUserType("coach")}>
              {lang === "sv" ? "Byt till ditt coach-konto" : "Switch to your coach account"}
            </div>
          }
        </>

      ) : (
        <>
          <div className="mt-6 z-heading2">
            {t("professional-details")}
          </div>

          <div className="mt-6 text-gray-400">
            {t("coaching-experience")}
          </div>
        </>
      )}

      <div className="flex items-center justify-between">
        <div
          className="mt-6 relative cursor-pointer bg-white rounded-full"
          onClick={() => setEditPicture(true)}
        >
          <img
            className={"picture-lg " + (picture ? "" : "p-3")}
            alt="pic"
            src={(picture ? picture : profileIcon)}
          />
          <div
            className="ml-4 flex item-center justify-center cursor-pointer rounded-[20px] hover:bg-indigo-700 bg-indigo-800 p-1"
            style={{
              position: "absolute",
              zIndex: 10,
              bottom: "0px",
              right: "0px",
            }}>
            <img alt="pic-icon" src={pictureCircleIcon}/></div>
        </div>
        <div>&nbsp;</div>
      </div>
      {
        props.profile.clientName &&
          <div className="mt-4">{props.profile.clientName}</div>
      }

      <div className="mt-6">{t("title-role")}</div>

      <input
        value={title}
        placeholder={t("title-placeholder")}
        className="z-input mt-2"
        onChange={(e) => setTitle(e.target.value)}
      />

      <div className="mt-6">{lang === "sv" ? "Rollbeskrivning" : "Role description"}</div>

      <textarea
        value={roleDescription}
        placeholder={lang === "sv" ? "Beskriv dina arbetsuppgifter" : "Describe your work tasks"}
        className="z-input mt-2"
        onChange={(e) => setRoleDescription(e.target.value)}
      />

      <div className="mt-6 flex flex-row items-center gap-1 p-1">
        <p>{t("select-leadership-level")}</p>
        <div onClick={() => setShowInfoModal(true)}>
          <img src={infoIcon} alt="Info" className="h-6 w-6 hover:bg-indigo-700 rounded-[20px] p-1"/>
        </div>
      </div>

      <select
        className="mt-2 z-select"
        value={leadershipLevel}
        onChange={(e) => setLeadershipLevel(e.target.value)}
      >
        <option value="">{t("select")}</option>
        {leadershipLevels.map((option, index) => (
          <option key={"level_" + index} value={option}>
            {t(option)}
          </option>
        ))}
      </select>

      <div className="mt-6">{t("in-company-since")}</div>

      <select
        className="mt-2 z-select"
        value={inCompanySince}
        onChange={(e) => setInCompanySince(parseInt(e.target.value))}
      >
        <option value={0}>{t("select")}</option>
        {makeRange(thisYear, 1990).map((year) => (
          <option key={"year_" + year} value={year}>
            {year}
          </option>
        ))}
      </select>

      <div className="mt-6">{t("current-role-since")}</div>

      <select
        className="mt-2 z-select"
        value={currentRoleSince}
        onChange={(e) => setCurrentRoleSince(parseInt(e.target.value))}
      >
        <option value={0}>{t("select")}</option>
        {makeRange(thisYear, inCompanySince > 0 ? inCompanySince : 1990).map(
          (year) => (
            <option key={"year_" + year} value={year}>
              {year}
            </option>
          )
        )}
      </select>

      <div className="mt-6">{lang === "sv" ? "Mer om dig" : "More about you"}</div>

      <textarea
        value={aboutMe}
        placeholder={lang === "sv" ? "Skriv en kort beskrivning av dig själv, så att AI-assistenten kan ge dig bättre svar." :
          "What else should your assistant know about you? Write a short descriprion about yourself so the AI can personalize its support."}
        className="z-input mt-2"
        onChange={(e) => setAboutMe(e.target.value)}
      />

      {
        props.standalone &&
          <ClientFiles lang={lang} customerFiles={props.customerFiles} />
      }

      <div className="flex flex-col gap-4">
        <CustomButton
          disabled={notValid()}
          className="mt-6"
          onClick={() => save()}
        >
          {props.standalone ? t("save") : t("next")}
        </CustomButton>

        {/* Back Link */}
        {!props.standalone && (
          <TextLink
            color="white"
            onClick={() => navigate(-1)}
            leftIcon={<MaskedIcon src={chevronLeftIcon} className="h-4 w-4"/>}
            size="sm"
          >
            {t("back")}
          </TextLink>
        )}
      </div>

      <LeadershipInfoInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />
    </>
  );
};

export default Profile;